import React, { useState, useEffect, useMemo, useContext } from "react";
import {
  PrimaryButton,
  DefaultButton,
  Icon
} from "@fluentui/react";
import Select from "react-select";
import FroalaEditor from "react-froala-wysiwyg";
import { t } from "i18next";
import MultiSelectClientDropdown from "./Components/MultiSelectClientDropdown";

import {
  getLLMAgentList,
  getFirmsList,
  getTemplatesListByFirmId,
  addAgenticTemplate,
  updateAgenticTemplate,
  deleteAgenticTemplateApi
} from "../../service/MasterService";

import themeContext   from "../../context/theme/ThemeContext";
import loaderContext  from "../../context/loader/LoaderContext";
import toastContext   from "../../context/toast/ToastContext";
import userContext    from "../../context/users/UserContext";

import CustomBreadCrumb         from "../../components/CustomBreadCrumb";
import { constantStyleForSelect } from "../../components/CustomSelect";
import { TOAST_TYPE }             from "../../constants/Constants";

import "./MyTemplate.css";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { getEnv } from '../../helpers/EnvHelper';

const froalaConfig = {
    placeholderText: "Add your template here…",
    key: getEnv("REACT_APP_FROALA_API_KEY"),
    charCounterCount: false,
    attribution: false,
    height: "65vh",
    width: "100%",
    fontSize: ["8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "24", "30", "36", "48", "60", "72", "96"],
    fontSizeDefault: "12",
    fontSizeUnit: 'px',
    pluginsEnabled: ["fullscreen", "codeBeautifier", "colors", "fontFamily", "fontSize", "paragraphFormat", "paragraphStyle", "align", "lists", "table", "wordPaste"],
    toolbarButtons: {
      moreText: {
        buttons: ["undo", "redo", "|", "paragraphFormat", "|", "fontFamily", "|", "fontSize", "|", "bold", "italic", "underline", "strikeThrough", "clearFormatting", "|", "textColor", "backgroundColor", "|", "alignLeft", "alignCenter", "alignRight", "alignJustify", "formatOL", "formatUL", "outdent", "indent", "|", "insertTable", "|", "copy"],
        align: "left",
        buttonsVisible: 10,
      },
    },
};

const MyTemplate = () => {
  const { getThemeColor, getAlterColor, themeName } = useContext(themeContext);
  const { setLoaderRunning, ref }        = useContext(loaderContext);
  const { showToastv2 }                  = useContext(toastContext);
  const { loadByUsername, authenticated }= useContext(userContext);

  // --- 1. KEEPING THE BACKGROUND FIX (Working for Light & Dark) ---
  const isLightMode = themeName === "Light" || themeName === "light";
  const alterBg = getAlterColor ? getAlterColor("background-color") : null;
  const themeBg = getThemeColor("background");
  const mainBackgroundColor = isLightMode ? "#ffffff" : (alterBg || themeBg || "#000000");

  // --- 2. REVERTING BORDER LOGIC TO ORIGINAL ---
  // We use the theme value directly, exactly as in your commented code.
  const borderColor = getThemeColor("border"); 

  // --- 3. KEEPING TEXT FIX ---
  const themeText = getThemeColor("text");
  const textColor = themeText ? String(themeText) : "inherit";

  // --- 4. ACCENT COLOR ---
  const accentBlueRaw = getThemeColor("active-side-tab");
  const accentBlue = accentBlueRaw ? String(accentBlueRaw) : "#E6F2FF"; 

  const [agentOptions, setAgentOptions] = useState([]);
  const [firmOptions,  setFirmOptions]  = useState([]);
  const [rows,         setRows]         = useState([]);
  const [selectedId,   setSelectedId]   = useState(null);
  const [draft,        setDraft]        = useState(null);
  const [editorValue,  setEditorValue]  = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletedRow, setDeletedRow] = useState({});
  
  useEffect(() => {
    if (!authenticated) loadByUsername();
    init();
  }, [authenticated]);

  const init = async () => {
    setLoaderRunning(true); ref.current.continuousStart();
    try {
      const [agentRes, firmRes, tmplRes] = await Promise.all([
        getLLMAgentList(),
        getFirmsList(),
        getTemplatesListByFirmId()
      ]);
      setAgentOptions(
        (agentRes.data?.data ?? []).map(a => ({
          value: a.id,
          label: a.title || "Unknown Agent"
        })).sort((a, b) => a.label.localeCompare(b.label))
      );

      let firmMap =[{ value: -1, label: "Default" }];
       firmMap.push(
        ...(firmRes.data?.data ?? []).map(f => ({
          value: f.id,
          label: f.name || "Unknown Firm"
        }))
      );
      setFirmOptions(firmMap);

      const flat = [];
      (tmplRes.data?.data ?? []).forEach(record => {
        const agentId = record.agentId;
        const agenticReportTemplateData = record.agenticReportTemplate
          flat.push({
            id: `${agentId}-${agenticReportTemplateData.clientIdList.join("-")}`,
            agentId: agentId,
            clientIds: agenticReportTemplateData.clientIdList.map( id => id === null ? -1 : id),
            content: agenticReportTemplateData.templateContent || "",
            templateId : agenticReportTemplateData.templateId
          });
      });
      setRows(flat);
    } catch (err) {
      console.error(err);
      showToastv2(TOAST_TYPE.ERROR, "Initial load failed");
    } finally {
      setLoaderRunning(false); ref.current.complete();
    }
  };

  const agentLbl = useMemo(
    () => new Map(agentOptions.map(o => [o.value, o.label])),
    [agentOptions]
  );
  const firmLbl = useMemo(
    () => new Map(firmOptions.map(o => [o.value, o.label])),
    [firmOptions]
  );

  const pickRow = row => {
    setSelectedId(row.id);
    setDraft({ ...row });
    setEditorValue(row.content);
    setTemplateId(row.templateId);
    setIsEditing(false);
  };

  const startEditRow = row => {
    setSelectedId(row.id);
    setDraft({ ...row });
    setEditorValue(row.content);
    setTemplateId(row.templateId);
    setIsEditing(true);
  };

  const startAddRow = () => {
    if(isEditing) return;
    const id = `new_${Date.now()}`;
    const empty = { id, agentId: null, clientIds: [], content: "" , templateId: null};
    setRows(prev => [empty, ...prev]);
    setIsEditing(true);
    setSelectedId(id);            
    setDraft(empty);
    setEditorValue("");
    setTemplateId(null);
  };

  const handleCancel = () => {
    if (draft?.id.startsWith("new_")) {
      setRows(prev => prev.filter(r => r.id !== draft.id));
      setSelectedId(null);
    }
    setEditorValue(draft.content);
    setIsEditing(false);
  };

  const updateDraft = (field, value) =>
    setDraft(d => ({ ...d, [field]: value }));

  const resetEditorState = () => {
    setIsEditing(false);
    setDraft(null);
    setTemplateId(null);
  };

  const saveDraft = async () => {
    if (!draft.agentId)  return showToastv2("error", "Select an Agent");
    if (draft.clientIds.length === 0)
      return showToastv2("error", "Select at least one Client");
    if (!editorValue.trim())
      return showToastv2("error", "Template content required");

    const payload = {
      agentId: draft.agentId,
      agenticReportTemplate: 
        { templateContent: editorValue, clientIdList: draft.clientIds, templateId: templateId }
    };

    setLoaderRunning(true); ref.current.continuousStart();
    try {
      const isNew = draft.id.startsWith("new_");
      const res = isNew
        ? await addAgenticTemplate(payload)
        : await updateAgenticTemplate(payload);

      if (res.data?.result === "SUCCESS") {
        showToastv2("success", isNew ? "Created" : "Updated");
        await init();
        resetEditorState(); 
        setSelectedId(draft.id);
      } else {
        throw new Error(res.data?.message ?? "Request failed");
      }
    } catch (err) {
      console.error(err);
      showToastv2("error", err.message);
    } finally {
      setLoaderRunning(false); ref.current.complete();
    }
  };

   const deleteConfirmModal = (row) => {
    setDeletedRow(row);
    setShowDeleteConfirmModal(true);
  };

  const deleteRow = async () => {
    setShowDeleteConfirmModal(false);
    setLoaderRunning(true); 
    ref.current.continuousStart();
    try {
      const res = await deleteAgenticTemplateApi(deletedRow.agentId, deletedRow.templateId);
      if (res.data?.result === "SUCCESS") {
        showToastv2("success", "Deleted"); 
        await init();
      } else throw new Error(res.data?.message ?? "Failed");
      
    } catch (err) {
      showToastv2("error", err.message);
      setShowDeleteConfirmModal(false);
    } finally {
      setLoaderRunning(false); 
      ref.current.complete();
    }
  };

  const getDisabledOptions = (selectedValues) => {
    const hasValue1 = selectedValues.includes(-1);
    if (hasValue1) {
      // "Default" is selected — disable all others
      return firmOptions
        .filter(opt => opt.value !== -1)
        .map(opt => opt.value);
    }
    if (selectedValues.length > 0) {
      return [-1];
    }
    return [];
  };

  const empty = rows.length === 0;

  // Recreated the style object concept from your commented code to ensure consistency
  // but applying it inline to avoid the React Object error risk.
  const cellStyle = {
      // color: textColor
  };

  return (
    <div
      className="main-container-body"
      style={{ background: mainBackgroundColor }}
    >
      <div className="breadCrumb">
        <CustomBreadCrumb items={[{ text: t("Agentic Report Template") }]} />
      </div>

      {empty && (
        <div className="empty-state-wrapper">
          <p style={{color: textColor}}>{t("No templates configured yet")}</p>
          <PrimaryButton text={t("Add New Template")}  disabled={isEditing} onClick={startAddRow} />
        </div>
      )}

      {!empty && (
        <div className="split-pane">
          <div className="table-toolbar" style={{borderBottom: borderColor}}>
            <PrimaryButton text={t("Add New Template")} disabled={isEditing} onClick={startAddRow} />
          </div>
          {showDeleteConfirmModal && (
        <ConfirmModal
          handleConfirm={deleteRow}
          handleNotConfirm={() => {
            setShowDeleteConfirmModal(!showDeleteConfirmModal);
          }}
          showConfirmModal={showDeleteConfirmModal}
          message={t("string-literals.delete-agentic-report-template")}
          title={t("confirm-message.confirm-delete-template")}
          confirmButtonText={t("confirm-message.yes")}
        />
      )}
          <div className="content-row">
            <div className="table-pane">
              <table className="template-table">
                <thead>
                  <tr>
                    <th style={{ width: "28%" }}>{t("Agent")}</th>
                    <th>{t("Client(s)")}</th>
                    <th style={{ width: 90 }}>{t("Template")}</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map(r => {
                    const selected = r.id === selectedId;
                    const isNew    = r.id.startsWith("new_");

                    return (
                      <tr
                        key={r.id}
                        style={{ 
                          background: selected ? accentBlue : "transparent"
                        }}
                        onClick={(e) => {
                          if (isEditing && selectedId === r.id) return; 
                          if (!isEditing) pickRow(r);
                        }}
                      >
                        <td>
                          {isNew? (
                            <Select
                              options={agentOptions}
                              value={agentOptions.find(o => o.value === draft?.agentId)}
                              onChange={opt => updateDraft("agentId", opt.value)}
                              styles={{
                                ...constantStyleForSelect(getThemeColor),
                                singleValue: (provided) => ({
                                  ...provided,
                                  color: textColor, 
                                }),
                                input: (provided) => ({
                                  ...provided,
                                  color: textColor,
                                }),
                              }}
                            />
                          ) : (
                            agentLbl.get(r.agentId)
                          )}
                        </td>

                        <td>
                          {selectedId === r.id && isEditing ? (
                            <MultiSelectClientDropdown
                              options={firmOptions}
                              value={firmOptions.filter(o =>
                                (draft?.clientIds ?? []).includes(o.value)
                              )}
                              onChange={(selectedOptions) =>
                                updateDraft("clientIds", selectedOptions.map(a => a.value))
                              }
                              placeholder="Select clients..."
                              getThemeColor={getThemeColor}
                              disabledOptions={getDisabledOptions(draft?.clientIds ?? [])}
                              addSelectAll={false}
                            />
                          ) : (
                            (() => {
                              const labels = r.clientIds.map(id => firmLbl.get(id)).filter(Boolean);
                              const displayCount = 2; 
                              const visible = labels.slice(0, displayCount);
                              const hiddenCount = labels.length - displayCount;
                              return hiddenCount > 0
                                ? `${visible.join(", ")}, +${hiddenCount} more`
                                : visible.join(", ");
                            })()
                          )}
                        </td>

                        { !isNew && !(selectedId === r.id && isEditing) && <td style={{ textAlign: "center" }}>
                          <Icon
                            iconName="Edit"
                            styles={{ root: { cursor: isEditing || isNew ? "not-allowed":"pointer", marginRight: 8, color: textColor } }}
                            onClick={e => {
                              if(isEditing) return;
                               e.stopPropagation(); startEditRow(r);
                               }}
                          />
                          <Icon
                            iconName="Delete"
                            styles={{ root: { cursor: isEditing || isNew ? "not-allowed":"pointer", color: textColor } }}
                            onClick={e => { 
                              if(isEditing) return;
                              deleteConfirmModal(r) }}
                          />
                        </td>}
                         {(isNew || (selectedId === r.id && isEditing)) && <td></td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="editor-pane" style={{ background: accentBlue }}>
              {selectedId ? (
                isEditing ? (
                  <>
                    <div className="editor-container-edit">
                      <FroalaEditor
                        tag="textarea"
                        model={editorValue}
                        onModelChange={setEditorValue}
                        config={froalaConfig}
                      />
                    </div>
                    
                    <div className="editor-actions-edit">
                      <DefaultButton
                        text={t("Cancel")}
                        onClick={handleCancel}
                        styles={{ root: { border: borderColor } }}
                      />
                      <PrimaryButton text={t("Save")} onClick={saveDraft} />
                    </div>
                  </>
                ) : (
                  <div className="full-screen-preview">
                    {editorValue ? (
                      <div
                        className="reportTemplateHtmlContent"
                        dangerouslySetInnerHTML={{ __html: editorValue }}
                      />
                    ) : (
                      <div className="no-content-message">
                        No content available
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="no-selection-message">
                  <p>{t("Select a row to view / edit")}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTemplate;